import { useEffect } from 'react';
import { EventStatusFilterType } from 'contexts/eventStatus';
import { PAGINATION_PAGE_SIZE } from 'modules/events/basics/constants/list.constants';
import { PresenceOptions } from 'modules/events/basics/enums/events.enums';
import { MarketWithAll } from 'modules/events/basics/types/events.types';
import useLazyGetFullPrivateEvents from 'modules/events/graphql/queries/getLazyFullPrivateEvents';
import { DateRange } from 'yoda-ui/components/Form/DateRangePicker/DateRangePicker.types';

type Params = {
  isPast: boolean;
  market: MarketWithAll | null;
  showRsvpEvents: boolean;
  sortOrder?: 'asc' | 'desc';
  pageSize?: number;
  page?: number;
  eventStatus?: EventStatusFilterType;
  eventType?: PresenceOptions;
  dateRange?: DateRange;
};

const usePaginatedEvents = ({
  isPast,
  market,
  showRsvpEvents,
  sortOrder = 'asc',
  pageSize = PAGINATION_PAGE_SIZE,
  page = 0,
  eventStatus,
  eventType,
  dateRange,
}: Params) => {
  const { queryGetFullPrivateEvents, data, count, loading, error } = useLazyGetFullPrivateEvents();

  const offset = page * pageSize;

  useEffect(() => {
    queryGetFullPrivateEvents({
      variables: {
        source: 'backoffice',
        filters: {
          market: market === 'all' ? null : market,
          isPast,
          isRsvpType: showRsvpEvents,
          ...(eventStatus && eventStatus !== EventStatusFilterType.all && {
            status: eventStatus === EventStatusFilterType.draft
              ? [EventStatusFilterType.draft, EventStatusFilterType.ready]
              : [eventStatus],
          }),
          ...(eventType && eventType !== PresenceOptions.all && { presence: eventType }),
          ...(dateRange && dateRange[0] && dateRange[1] && {
            dateRange: { startDate: dateRange[0], endDate: dateRange[1] },
          }),
        },
        limit: pageSize,
        offset,
        sortOrder,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market, isPast, showRsvpEvents, sortOrder, pageSize, offset, eventStatus, eventType, dateRange]);

  return {
    data: data || [],
    loading,
    error,
    totalCount: count,
  };
};

export default usePaginatedEvents;
